import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { compact } from 'lodash-es';
import { DropdownMenu, useOverlayContext } from 'seagull';
import { ActivityLog, AgreementEdit, Copy, Document, Download, EditSquare, LinkIcon, Trash, User, WalletCheck } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import ClientViewMenuItem from '~/components/ClientViewMenuItem/ClientViewMenuItem';
import { routes } from '~/constants';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import ShareAgreementLinksModal from './ShareAgreementLinksModal';
const deprecatedTestIds = {
    AgreementRowMenuItemsTestIds: {
        EditAgreement: (testId)=>"".concat(testId, "-edit-agreement"),
        RenameAgreement: (testId)=>"".concat(testId, "-rename-agreement"),
        ChangeAssignee: (testId)=>"".concat(testId, "-change-assignee"),
        Duplicate: (testId)=>"".concat(testId, "-duplicate"),
        DownloadPdf: (testId)=>"".concat(testId, "-download-pdf"),
        DirectLinks: (testId)=>"".concat(testId, "-direct-links"),
        ViewClientPortal: (testId)=>"".concat(testId, "-view-client-portal"),
        BillingHub: (testId)=>"".concat(testId, "-billing-hub"),
        Invoices: (testId)=>"".concat(testId, "-invoices"),
        Activity: (testId)=>"".concat(testId, "-activity"),
        Terminate: (testId)=>"".concat(testId, "-terminate")
    }
};
export const GetAgreementRowMenuItemsTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds
    });
export function useAgreementRowMenuItems(param) {
    let { agreement, isVendor, isTerminated, readOnly = false, testId, heapId, onEditAgreement, onRenameAgreement, onRenameSuccess, onChangeAssignee, onDuplicateAgreement, onDownloadPDF, onTerminateAgreement } = param;
    const { t: rowActionTexts } = useLocaleKeys('agreement.dashboard.rowActions');
    const overlayContext = useOverlayContext();
    const { AgreementRowMenuItemsTestIds } = useMemo(()=>GetAgreementRowMenuItemsTID(), []);
    return compact([
        isVendor && !isTerminated && !readOnly && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: Intent.EditProposal,
            iconStart: /*#__PURE__*/ _jsx(AgreementEdit, {}),
            disabled: agreement.isSample,
            testId: AgreementRowMenuItemsTestIds.EditAgreement(testId),
            onClick: ()=>onEditAgreement(agreement),
            bi: {
                name: 'edit_agreement'
            },
            children: rowActionTexts.editAgreement()
        }, "edit-agreement-".concat(agreement.id)),
        isVendor && !readOnly && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: Intent.EditProposal,
            iconStart: /*#__PURE__*/ _jsx(EditSquare, {}),
            disabled: agreement.isSample,
            testId: AgreementRowMenuItemsTestIds.RenameAgreement(testId),
            onClick: ()=>onRenameAgreement(agreement, onRenameSuccess),
            bi: {
                name: 'rename_agreement'
            },
            children: rowActionTexts.renameAgreement()
        }, "rename-agreement-".concat(agreement.id)),
        isVendor && !readOnly && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: Intent.EditProposal,
            iconStart: /*#__PURE__*/ _jsx(User, {}),
            testId: AgreementRowMenuItemsTestIds.ChangeAssignee(testId),
            onClick: ()=>onChangeAssignee(agreement),
            bi: {
                name: 'change_assignee'
            },
            children: rowActionTexts.changeAssignee()
        }, "change-assignee-".concat(agreement.id)),
        isVendor && !readOnly && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: Intent.DuplicateAgreement,
            iconStart: /*#__PURE__*/ _jsx(Copy, {}),
            disabled: agreement.isSample,
            testId: AgreementRowMenuItemsTestIds.Duplicate(testId),
            "data-heapid": "".concat(heapId, "-menu-duplicate"),
            onClick: ()=>onDuplicateAgreement(agreement),
            bi: {
                name: 'duplicate_agreement'
            },
            children: rowActionTexts.duplicateAgreement()
        }, "duplicate-".concat(agreement.id)),
        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: Intent.DownloadAgreementPdf,
            iconStart: /*#__PURE__*/ _jsx(Download, {}),
            testId: AgreementRowMenuItemsTestIds.DownloadPdf(testId),
            "data-heapid": "".concat(heapId, "-menu-download-pdf"),
            onClick: ()=>onDownloadPDF(agreement),
            bi: {
                name: 'download_pdf'
            },
            children: rowActionTexts.downloadPdf()
        }, "download-pdf-".concat(agreement.id)),
        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: Intent.CopyRelationshipDirectLink,
            iconStart: /*#__PURE__*/ _jsx(LinkIcon, {}),
            testId: AgreementRowMenuItemsTestIds.DirectLinks(testId),
            onClick: ()=>{
                overlayContext.showModal({
                    biName: 'share_agreement_links_modal',
                    closeOnClickOutside: true,
                    closeOnEscapeKey: true,
                    content: (p)=>/*#__PURE__*/ _jsx(ShareAgreementLinksModal, _object_spread_props(_object_spread({}, p), {
                            id: agreement.id,
                            customerName: agreement.customerName,
                            status: agreement.status
                        }))
                });
            },
            bi: {
                name: 'share_direct_link'
            },
            children: rowActionTexts.shareDirectLink()
        }, "direct-links-".concat(agreement.id)),
        isVendor && /*#__PURE__*/ _jsx(ClientViewMenuItem, {
            closeOnClick: true,
            clientBusinessId: agreement.customerBusinessId,
            testId: AgreementRowMenuItemsTestIds.ViewClientPortal(testId)
        }, "view-client-portal-".concat(agreement.id)),
        !isTerminated && !readOnly && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            as: Link,
            to: routes.agreementMinisiteRoutes(agreement.id).billingHub,
            iconStart: /*#__PURE__*/ _jsx(WalletCheck, {}),
            disabled: agreement.isSample,
            testId: AgreementRowMenuItemsTestIds.BillingHub(testId),
            "data-heapid": "".concat(heapId, "-menu-billing-hub"),
            bi: {
                name: 'manage_billing'
            },
            children: rowActionTexts.manageBilling()
        }, "billing-hub-".concat(agreement.id)),
        !isTerminated && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            as: Link,
            to: routes.agreementMinisiteRoutes(agreement.id).invoices,
            iconStart: /*#__PURE__*/ _jsx(Document, {}),
            disabled: agreement.isSample,
            testId: AgreementRowMenuItemsTestIds.Invoices(testId),
            "data-heapid": "".concat(heapId, "-menu-invoices"),
            bi: {
                name: 'view_invoices'
            },
            children: rowActionTexts.viewInvoices()
        }, "invoices-".concat(agreement.id)),
        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            as: Link,
            to: routes.agreementMinisiteRoutes(agreement.id).activityLog,
            iconStart: /*#__PURE__*/ _jsx(ActivityLog, {}),
            disabled: agreement.isSample,
            testId: AgreementRowMenuItemsTestIds.Activity(testId),
            "data-heapid": "".concat(heapId, "-menu-activity"),
            bi: {
                name: 'view_activity_log'
            },
            children: rowActionTexts.viewActivityLog()
        }, "activity-".concat(agreement.id)),
        isVendor && !isTerminated && !readOnly && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
            intent: Intent.TerminateAgreement,
            skin: "error",
            iconStart: /*#__PURE__*/ _jsx(Trash, {}),
            testId: AgreementRowMenuItemsTestIds.Terminate(testId),
            "data-heapid": "".concat(heapId, "-menu-terminate"),
            onClick: ()=>onTerminateAgreement(agreement),
            bi: {
                name: 'terminate_agreement'
            },
            children: rowActionTexts.terminateAgreement()
        }, "terminate-".concat(agreement.id))
    ]);
}
